import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { SearchInput } from '../components/ui/SearchInput';
import { apiList } from '../api/apiClient';
import { useAuth } from '../hooks/useAuth';
import { BulkActions, useSelection } from '../components/ui/BulkActions';
import { CountBand } from '../components/ui/CountBand';
import { deleteTenant } from '../api/tenants';
import { refreshAllCounters } from '../utils/refreshCounters';

const PER_PAGE_OPTIONS = [10, 50, 100, 250];

const redactPhone = (value: unknown): string => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 4 ? `••• ••• ${digits.slice(-4)}` : '—';
};

interface PageNavProps {
  safePage: number; totalPages: number; startIndex: number; endIndex: number;
  filteredLength: number; perPage: number;
  setPage: (fn: (p: number) => number) => void; setPerPage: (n: number) => void;
}
const PageNav: React.FC<PageNavProps> = ({ safePage, totalPages, startIndex, endIndex, filteredLength, perPage, setPage, setPerPage }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-500">Showing {filteredLength === 0 ? 0 : startIndex + 1}–{endIndex} of {filteredLength}</span>
      <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} className="text-sm border border-gray-300 rounded-lg px-2 py-1">
        {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
    <div className="flex items-center gap-1">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40">Previous</button>
      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
        let pn: number;
        if (totalPages <= 7) pn = i + 1;
        else if (safePage <= 4) pn = i + 1;
        else if (safePage >= totalPages - 3) pn = totalPages - 6 + i;
        else pn = safePage - 3 + i;
        return <button key={pn} onClick={() => setPage(() => pn)} className={`w-8 h-8 text-sm rounded-lg ${safePage === pn ? 'bg-brand-600 text-white' : 'hover:bg-gray-50 text-gray-700'}`}>{pn}</button>;
      })}
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40">Next</button>
    </div>
  </div>
);

export const Tenants: React.FC = () => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [tenantProperties, setTenantProperties] = useState<any[]>([]);
  const [activationsByTenant, setActivationsByTenant] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [propertyFilter, setPropertyFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'customer';
  const { selected, toggle, toggleAll, clear: clearSelection } = useSelection();

  useEffect(() => {
    const load = async () => {
      try {
        const [tenantData, unitData, propData, tpData, actData] = await Promise.all([
          apiList('tenants').catch(() => []),
          apiList('units').catch(() => []),
          apiList('properties').catch(() => []),
          apiList('tenant_properties').catch(() => []),
          apiList('app_activations').catch(() => []),
        ]);
        const actMap: Record<string, any[]> = {};
        (actData || []).forEach((a: any) => {
          const tid = String(a.tenant_id || a.person_id || '');
          if (!tid) return;
          (actMap[tid] = actMap[tid] || []).push(a);
        });
        setActivationsByTenant(actMap);

        setAllProperties(propData || []);
        setTenantProperties(tpData || []);

        // Build lookup maps by uuid
        const propMap: Record<string, any> = {};
        (propData || []).forEach((p: any) => { propMap[p.uuid] = p; propMap[p.id] = p; });
        const unitMap: Record<string, any> = {};
        (unitData || []).forEach((u: any) => { unitMap[u.uuid] = u; unitMap[u.id] = u; });

        // /people now fans out junction-linked tenants into one row per
        // linked property (canonical + N synthetic) so kiosk / visitor
        // clients filtering by property_id can find them. The admin grid
        // wants ONE row per person — drop synthetic copies (those with
        // `_from_junction === true`) and fall back to dedup-by-id.
        const seenIds = new Set<string>();
        const dedupedTenants = (tenantData || []).filter((t: any) => {
          if (t?._from_junction) return false;
          const k = String(t?.id ?? '');
          if (!k || seenIds.has(k)) return false;
          seenIds.add(k);
          return true;
        });
        const enriched = dedupedTenants.map((t: any) => {
          const unit = unitMap[t.unit_id] || {};
          const prop = propMap[t.property_id] || propMap[unit.property_id] || {};
          // Find all properties via tenant_properties
          const myTPs = (tpData || []).filter((tp: any) => tp.tenant_id === t.uuid || tp.tenant_id === String(t.id));
          const myPropertyNames = myTPs.map((tp: any) => propMap[tp.property_id]?.name).filter(Boolean);
          // If no tenant_properties, use the direct property_id
          if (myPropertyNames.length === 0 && prop.name) myPropertyNames.push(prop.name);

          return {
            ...t,
            first_name: t.first_name || t.name?.split(' ')[0] || '',
            last_name: t.last_name || t.name?.split(' ').slice(1).join(' ') || '',
            unit_number: t.unit_number || unit.unit_number || '',
            property_name: myPropertyNames[0] || prop.name || '',
            property_count: myPropertyNames.length,
            all_property_names: myPropertyNames,
          };
        });

        setTenants(enriched);
      } catch (e) {
        console.error('Failed to load people:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [role, user]);

  const propertyOptions = useMemo(() => {
    const seen = new Map<string, string>();
    allProperties.forEach((p: any) => {
      const id = p.uuid || String(p.id);
      if (id && p.name) seen.set(id, p.name);
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allProperties]);

  const activeTenants = useMemo(() => tenants.filter((t: any) =>
    !t.status || String(t.status).toLowerCase() === 'active'
  ), [tenants]);

  const filtered = useMemo(() => {
    let list = activeTenants;

    if (propertyFilter !== 'all') {
      list = list.filter((t: any) => {
        if (t.property_id === propertyFilter) return true;
        const myTPs = tenantProperties.filter((tp: any) =>
          tp.tenant_id === t.uuid || tp.tenant_id === String(t.id)
        );
        return myTPs.some((tp: any) => tp.property_id === propertyFilter);
      });
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((t: any) =>
        `${t.first_name} ${t.last_name} ${t.email || ''} ${t.property_name || ''}`.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeTenants, search, propertyFilter, tenantProperties]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, filtered.length);
  const paginatedData = filtered.slice(startIndex, endIndex);

  useEffect(() => { setPage(1); }, [search, perPage, propertyFilter]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;

  return (
    <div className="shadow-list-page prototype-page people-list-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-gray-900">People</h1><p className="text-gray-500 mt-1">{activeTenants.length} active people</p></div>
      </div>
      <div className="prototype-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search people..." />
        <label>
          <small>Property</small>
          <select value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}>
            <option value="all">All Properties ({propertyOptions.length})</option>
            {propertyOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </div>
      <CountBand value={filtered.length} detail="People in the current accessible portfolio" />
      <Card padding={false}>
        <div className="legacy-table-controls p-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1"><SearchInput value={search} onChange={setSearch} placeholder="Search people..." /></div>
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white md:w-64"
          >
            <option value="all">All Properties ({propertyOptions.length})</option>
            {propertyOptions.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <PageNav safePage={safePage} totalPages={totalPages} startIndex={startIndex} endIndex={endIndex} filteredLength={filtered.length} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />

        {/* Mobile: card grid (no list/table on small screens). */}
        <div className="md:hidden grid grid-cols-1 gap-3 p-4">
          {paginatedData.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">No people found</div>
          ) : (
            paginatedData.map((t: any) => (
              <div key={t.id} onClick={() => navigate(`/tenants/${t.uuid || t.id}`)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
                <div className="flex items-start justify-between mb-1 gap-2">
                  <div className="min-w-0">
                    <h3 className="entity-name font-semibold truncate">{t.first_name} {t.last_name}</h3>
                    {t.email && <p className="text-xs text-gray-500 truncate">{t.email}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Badge status={t.status || 'active'}>{t.status || 'active'}</Badge>
                  </div>
                </div>
                <div className="customer-card-address"><span>Property</span><p className="entity-name">{t.property_name || 'No property assigned'}{t.property_count > 1 ? ` +${t.property_count - 1} more` : ''}</p></div>
                {(() => {
                  const phone = t.phone || t.phone_number || t.mobile_phone || t.cell_phone || t.contact_phone;
                  return <div className="customer-card-contact"><span>Contact</span><strong>{t.email || 'No email on file'}</strong>{phone && <a href={`tel:${phone}`} onClick={(e) => e.stopPropagation()}>{phone}</a>}</div>;
                })()}
                {(() => {
                  const acts = activationsByTenant[String(t.uuid || t.id)] || activationsByTenant[String(t.id)] || [];
                  const activated = acts.some((a: any) => a.activated === true || !!a.activated_at);
                  return <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}><summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary><div><article><span>Properties</span><strong>{t.property_count || 0}</strong></article><article><span>Unit</span><strong>{t.unit_number || '—'}</strong></article><article><span>Face ID</span><strong className="metric-text-value">{t.face_id_enrolled ? 'Enrolled' : 'Not enrolled'}</strong></article><article><span>Mobile</span><strong className="metric-text-value">{activated ? 'Activated' : 'Not activated'}</strong></article></div></details>;
                })()}
                <div className="mobile-card-actions">
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/tenants/${t.uuid || t.id}`); }} className="row-open">Open <span aria-hidden="true">→</span></button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop: full table (hidden on small screens). */}
        <div className="hidden md:block">
        <Table
          columns={[
            { key: '_select', header: <input type="checkbox" checked={selected.size === paginatedData.length && paginatedData.length > 0} onChange={() => toggleAll(paginatedData.map((d: any) => d.id))} className="w-4 h-4 rounded border-gray-300" />, render: (t: any) => <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} onClick={(e: any) => e.stopPropagation()} className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />, className: 'w-8' },
            { key: 'name', header: 'Name', render: (t: any) => (
              <div>
                <span className="entity-name font-medium">{t.first_name} {t.last_name}</span>
              </div>
            )},
            { key: 'email', header: 'Email', render: (t: any) => t.email || <span className="text-gray-400">—</span> },
            { key: 'phone', header: 'Phone', render: (t: any) => redactPhone(t.phone || t.phone_number || t.mobile_phone || t.cell_phone || t.contact_phone) },
            { key: 'property_name', header: 'Property', render: (t: any) => (
              <div>
                <span className="entity-name">{t.property_name || '—'}</span>
                {t.property_count > 1 && <span className="ml-1 text-xs text-gray-500">+{t.property_count - 1} more</span>}
              </div>
            )},
            { key: 'face', header: 'Face ID', render: (t: any) => (
              t.face_id_enrolled
                ? <Badge color="green">Enrolled</Badge>
                : <span className="text-xs text-gray-400">Not enrolled</span>
            )},
            { key: 'portal', header: 'Tenant Portal', render: (t: any) => {
              const acts = activationsByTenant[String(t.uuid || t.id)] || activationsByTenant[String(t.id)] || [];
              const activated = acts.some((a: any) => a.activated === true || !!a.activated_at);
              return activated
                ? <Badge color="green">Activated</Badge>
                : <span className="text-xs text-gray-400">Not activated</span>;
            }},
            { key: 'status', header: 'Status', render: (t: any) => <Badge status={t.status || 'active'}>{t.status || 'active'}</Badge> },
            { key: '_actions', header: '', render: (t: any) => (
              <button className="row-open" onClick={(e) => { e.stopPropagation(); navigate(`/tenants/${t.uuid || t.id}`); }}>Open <span aria-hidden="true">→</span></button>
            ), className: 'w-24' },
          ]}
          data={paginatedData}
          onRowClick={(t: any) => navigate(`/tenants/${(t as any).uuid || t.id}`)}
          emptyMessage="No people found"
        />
        </div>
        <PageNav safePage={safePage} totalPages={totalPages} startIndex={startIndex} endIndex={endIndex} filteredLength={filtered.length} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
      </Card>
      <BulkActions selectedIds={selected} onClearSelection={clearSelection} onDelete={async (ids) => { await Promise.all(ids.map(id => deleteTenant(id))); refreshAllCounters(); window.location.reload(); }} entityName="people" />
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Remove Person">
        <p className="text-sm text-gray-600 mb-4">
          Remove <span className="font-semibold">{confirmDelete?.first_name} {confirmDelete?.last_name}</span>? This deletes their record, PIN, and app access. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</Button>
          <Button
            className="!bg-red-600 hover:!bg-red-700"
            disabled={deleting}
            onClick={async () => {
              if (!confirmDelete) return;
              setDeleting(true);
              try {
                await deleteTenant(confirmDelete.id);
                setTenants((prev) => prev.filter((x) => x.id !== confirmDelete.id));
                refreshAllCounters();
                setConfirmDelete(null);
              } catch (e: any) {
                alert(`Failed to remove: ${e?.message || e}`);
              } finally {
                setDeleting(false);
              }
            }}
          >{deleting ? 'Removing…' : 'Remove'}</Button>
        </div>
      </Modal>
    </div>
  );
};
